/**
 * @file        src/pages/package/CategoryList.jsx
 * @description Paginated list of package categories with a name filter.
 *              First screen of the Package Management module.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Package Management slice 1).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

export default function CategoryList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState({ q: '' });
  const [applied, setApplied] = useState(filters);

  const qc = useQueryClient();
  const params = { ...applied, page, pageSize };
  const { data, isLoading } = useQuery({
    queryKey: qk.packageCategories.list(params),
    queryFn: () => get('/package-categories', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/package-categories/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Category removed.');
      qc.invalidateQueries({ queryKey: ['package-categories'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete category'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setApplied(filters);
  }

  async function onDelete(row) {
    if (!(await confirm(`Delete category “${row.name}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Category List"
        subtitle="Package Management · package categories"
        actions={
          <Link to="/package/categories/new" className="btn primary"><Icon name="plus" /> Add Category</Link>
        }
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={applyFilters}>
          <div className="field">
            <label>Name / Slug</label>
            <input placeholder="e.g. Honeymoon" value={filters.q}
                   onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Slug</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={5} className="empty">No categories yet. Add your first one.</td></tr>
                ) : rows.map((c, idx) => (
                  <tr key={c.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{c.name}</b>{c.description ? <small className="muted db">{c.description}</small> : null}</td>
                    <td className="mono">{c.slug}</td>
                    <td>
                      <Chip tone={c.is_active ? 'ok' : 'rej'}>{c.is_active ? 'Active' : 'Inactive'}</Chip>
                    </td>
                    <td className="row-act">
                      <Link to={`/package/categories/${c.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(c)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
